// Image-sampled density + optional per-dot colour — ported from the Media
// Studio Image tab (`densityFor`, `colorAtFor`, `lum`).

import type { HalftoneImgMode, PatternParams, RgbTriplet, SampledImage } from '../../types';
import { clamp } from '../dot-engine/math';
import { averageColor, sampleImageBrightness } from '../image-sample';
import { type CoverMapping, canvasToImageUv, computeCoverMapping } from './cover-map';

function applyContrast(l: number, contrast: number): number {
  return clamp((l - 0.5) * contrast + 0.5, 0, 1);
}

function rgbToHex([r, g, b]: RgbTriplet): string {
  const ri = Math.round(clamp(r, 0, 255));
  const gi = Math.round(clamp(g, 0, 255));
  const bi = Math.round(clamp(b, 0, 255));
  return `#${ri.toString(16).padStart(2, '0')}${gi.toString(16).padStart(2, '0')}${bi.toString(16).padStart(2, '0')}`;
}

export interface ImageHalftoneSampling {
  readonly map: CoverMapping;
  readonly imgMode: HalftoneImgMode;
  readonly imgContrast: number;
  readonly imgCutoff: number;
  readonly canvasW: number;
  readonly canvasH: number;
}

export function buildImageHalftoneSampling(
  sampled: SampledImage,
  canvasW: number,
  canvasH: number,
  params: PatternParams,
): ImageHalftoneSampling {
  return {
    map: computeCoverMapping(
      canvasW,
      canvasH,
      sampled.sampleW,
      sampled.sampleH,
      params.cropAnchorX ?? 0.5,
      params.cropAnchorY ?? 0.5,
    ),
    imgMode: params.imgMode,
    imgContrast: params.imgContrast,
    imgCutoff: params.imgCutoff,
    canvasW,
    canvasH,
  };
}

function sampleLuminance(
  sampled: SampledImage,
  x: number,
  y: number,
  sampling: ImageHalftoneSampling,
): number {
  const { u, v } = canvasToImageUv(x, y, sampling.canvasW, sampling.canvasH, sampling.map);
  const raw = sampleImageBrightness(sampled, u, v);
  return applyContrast(raw, sampling.imgContrast);
}

/** Density at canvas coords — 0..1, drives dot radius in buildDots. */
export function imageDensityAt(
  sampled: SampledImage,
  x: number,
  y: number,
  sampling: ImageHalftoneSampling,
): number {
  const l = sampleLuminance(sampled, x, y, sampling);
  const floor = 1 - sampling.imgCutoff / 100;
  const d = sampling.imgMode === 'dark' ? l : 1 - l;
  if (sampling.imgMode === 'dark' && d < floor) return 0;
  return d;
}

const COLOR_NEIGHBORHOOD = 0.012;

/** Per-dot colour sampled from the source image. */
export function imageColorAt(
  sampled: SampledImage,
  x: number,
  y: number,
  sampling: ImageHalftoneSampling,
): string | null {
  const { u, v } = canvasToImageUv(x, y, sampling.canvasW, sampling.canvasH, sampling.map);
  const half = COLOR_NEIGHBORHOOD;
  const rgb = averageColor(sampled, u - half, u + half, v - half, v + half);
  return rgbToHex(rgb);
}
